import type { FC, ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '@pflegenavi/frontend/localization';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import type {
  IdvCheckReason,
  IdvPsc,
  IdvPscStatus,
  IdvSession,
  IdvSessionStatus,
} from '@pflegenavi/shared/api';
import type {
  IdvActions,
  IdvPscAction,
} from '@pflegenavi/shared-frontend/platform';
import { KYC_RENEWAL_WARNING_DAYS } from '@pflegenavi/shared-frontend/platform';
import type { LabelColor } from '../Label';
import { Label } from '../Label';
import { CloseModalIcon } from '../CloseModalIcon';

export type KycIdvSubject = 'legal-entity' | 'service-provider';

export interface KycIdvDialogViewProps {
  open: boolean;
  onClose: () => void;
  /** Shown under the title, e.g. the legal entity name. */
  subtitle?: string;
  subject: KycIdvSubject;
  kycVerified: boolean;
  renewalDeadline: string | null;
  /** Negative once the deadline has passed. */
  renewalDaysLeft: number | null;
  lastKycDate: string | null;
  sessionStatus: IdvSessionStatus | null;
  session: IdvSession | undefined;
  waitingForSession: boolean;
  actions: IdvActions;
  /** A redirect is in flight; disables all actions. */
  pending: boolean;
  onPrimaryAction: () => void;
  onPscAction: (action: IdvPscAction) => void;
  onCopyPscLink: (url: string) => void;
}

type StatusColor = Extract<
  LabelColor,
  'default' | 'info' | 'success' | 'warning' | 'error'
>;

const PSC_COLORS: Record<IdvPscStatus, StatusColor> = {
  PENDING_VALIDATION: 'info',
  VALIDATED: 'success',
  REJECTED: 'error',
  ABANDONED: 'default',
};

const getStatus = (
  kycVerified: boolean,
  renewalDaysLeft: number | null,
  sessionStatus: IdvSessionStatus | null,
  awaitingConfirmation: boolean
): { color: StatusColor; labelKey: string } => {
  if (awaitingConfirmation) {
    return { color: 'info', labelKey: 'kyc.idv.status.awaiting-confirmation' };
  }
  if (sessionStatus === 'PENDING' || sessionStatus === 'REVIEW') {
    return {
      color: 'info',
      labelKey: `kyc.idv.session-status.${sessionStatus}`,
    };
  }
  if (!kycVerified) {
    return sessionStatus && sessionStatus !== 'VALIDATED'
      ? {
          color: sessionStatus === 'REFUSED' ? 'error' : 'default',
          labelKey: `kyc.idv.session-status.${sessionStatus}`,
        }
      : { color: 'default', labelKey: 'kyc.idv.status.not-verified' };
  }
  if (renewalDaysLeft !== null && renewalDaysLeft < 0) {
    return { color: 'error', labelKey: 'kyc.idv.status.renewal-overdue' };
  }
  if (renewalDaysLeft !== null && renewalDaysLeft <= KYC_RENEWAL_WARNING_DAYS) {
    return { color: 'warning', labelKey: 'kyc.idv.status.renew-soon' };
  }
  return { color: 'success', labelKey: 'kyc.idv.status.verified' };
};

export const KycIdvDialogView: FC<KycIdvDialogViewProps> = ({
  open,
  onClose,
  subtitle,
  subject,
  kycVerified,
  renewalDeadline,
  renewalDaysLeft,
  lastKycDate,
  sessionStatus,
  session,
  waitingForSession,
  actions,
  pending,
  onPrimaryAction,
  onPscAction,
  onCopyPscLink,
}) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  const formatDay = (date: string) =>
    formatDate(new Date(date), YEAR_MONTH_DAY_SHORT_FORMAT);

  const status = getStatus(
    kycVerified,
    renewalDaysLeft,
    sessionStatus,
    actions.awaitingConfirmation
  );
  const renewalDue =
    kycVerified &&
    renewalDaysLeft !== null &&
    renewalDaysLeft <= KYC_RENEWAL_WARNING_DAYS;
  const inProgress = sessionStatus === 'PENDING' || sessionStatus === 'REVIEW';

  const description = waitingForSession
    ? null
    : actions.awaitingConfirmation
    ? t('kyc.idv.awaiting-confirmation-hint')
    : sessionStatus === 'REVIEW'
    ? t(
        actions.pscActions.length > 0
          ? 'kyc.idv.review-pscs-hint'
          : 'kyc.idv.review-hint'
      )
    : actions.primary === null
    ? t('kyc.idv.complete-pscs-hint')
    : !kycVerified && !inProgress
    ? t(`kyc.idv.dialog.not-verified-description.${subject}`)
    : renewalDue && !inProgress
    ? t('kyc.idv.dialog.renewal-description')
    : null;

  const sessionDetails = session && (
    <SessionDetails
      session={session}
      pscActions={actions.pscActions}
      onPscAction={onPscAction}
      onCopyPscLink={onCopyPscLink}
      disabled={pending}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 2.5 }}>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Avatar
            variant="rounded"
            sx={
              status.color === 'default'
                ? { bgcolor: 'grey.300', color: 'grey.700' }
                : {
                    bgcolor: `${status.color}.lighter`,
                    color: `${status.color}.dark`,
                  }
            }
          >
            <GppMaybeOutlinedIcon />
          </Avatar>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography variant="h6" component="h2">
              {t('kyc.idv.dialog.title')}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                noWrap
                title={subtitle}
                sx={{
                  color: 'text.secondary',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ mr: -1.5 }}>
            <CloseModalIcon onClick={onClose} />
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Stack
            sx={{
              gap: 1.5,
              p: 2,
              borderRadius: 1.5,
              bgcolor: 'background.neutral',
            }}
          >
            <SummaryRow label={t('kyc.idv.status-label')}>
              <Label variant="ghost" color={status.color}>
                {t(status.labelKey)}
              </Label>
            </SummaryRow>
            {lastKycDate && (
              <SummaryRow
                label={t(
                  kycVerified
                    ? 'kyc.idv.verified-on'
                    : 'kyc.idv.last-verified-on'
                )}
              >
                {formatDay(lastKycDate)}
              </SummaryRow>
            )}
            {inProgress && session?.created_on && (
              <SummaryRow label={t('kyc.idv.started-on')}>
                {formatDay(session.created_on)}
              </SummaryRow>
            )}
            {kycVerified && renewalDeadline && (
              <SummaryRow label={t('kyc.idv.renew-by')}>
                {formatDay(renewalDeadline)}
              </SummaryRow>
            )}
            {kycVerified && renewalDaysLeft !== null && (
              <RenewalProgress daysLeft={renewalDaysLeft} />
            )}
            {waitingForSession && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
          </Stack>

          {description && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {description}
            </Typography>
          )}

          {sessionDetails &&
            (actions.collapseExisting ? (
              <Accordion
                disableGutters
                sx={{ '&::before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    {t('kyc.idv.previous-session')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack
                    sx={{
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t(`kyc.idv.session-status.${session.status}`)}
                    </Typography>
                    {sessionDetails}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ) : (
              sessionDetails
            ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <Button onClick={onClose}>
          {actions.primary === null
            ? t('actions.close')
            : !kycVerified && actions.primary === 'start'
            ? t('actions.cancel')
            : kycVerified && !renewalDue
            ? t('actions.close')
            : t('kyc.idv.later')}
        </Button>
        {!waitingForSession && actions.primary && (
          <Button
            variant="contained"
            onClick={onPrimaryAction}
            disabled={pending}
          >
            {actions.primary === 'continue'
              ? t('kyc.idv.continue-session')
              : kycVerified
              ? t('kyc.idv.renew-button')
              : sessionStatus === 'REFUSED'
              ? t('kyc.idv.restart-session')
              : t('kyc.idv.start-session')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const SummaryRow: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Stack
    direction="row"
    sx={{
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography variant="subtitle2" component="div">
      {children}
    </Typography>
  </Stack>
);

const RenewalProgress: FC<{ daysLeft: number }> = ({ daysLeft }) => {
  const { t } = useTranslation();

  if (daysLeft < 0) {
    return (
      <Typography
        variant="caption"
        sx={{
          color: 'error.dark',
        }}
      >
        {t('kyc.idv.renewal-overdue-hint')}
      </Typography>
    );
  }
  // full green bar outside the renewal window, draining amber inside it
  const color = daysLeft > KYC_RENEWAL_WARNING_DAYS ? 'success' : 'warning';
  return (
    <Stack
      sx={{
        gap: 0.75,
      }}
    >
      <LinearProgress
        variant="determinate"
        color={color}
        value={Math.min((daysLeft / KYC_RENEWAL_WARNING_DAYS) * 100, 100)}
      />
      <Typography variant="caption" color={`${color}.dark`}>
        {t('kyc.idv.days-left', { count: daysLeft })}
      </Typography>
    </Stack>
  );
};

// Documented Mangopay IDV checks and refusal reasons; unknown ones show their raw identifier
const KNOWN_CHECKS = [
  'IDENTITY_DOCUMENT_VERIFICATION',
  'IDV_NAME_MATCH_CHECK',
  'IDV_AGE_CHECK',
  'BUSINESS_VERIFICATION',
  'BUSINESS_NAME_MATCH',
  'BUSINESS_INSIGHTS_MATCH',
  'PSC_ROLE_CHECK',
  'PSC_CONSISTENCY_CHECK',
] as const;

const KNOWN_REASONS = [
  'DOCUMENT_NOT_ACCEPTED',
  'FACE_MATCH_FAIL',
  'DOCUMENT_FALSIFIED',
  'DOCUMENT_UNREADABLE',
  'DOCUMENT_HAS_EXPIRED',
  'DOCUMENT_TYPE_NOT_SUPPORTED',
  'DOCUMENT_INCOMPLETE',
  'REGISTRY_DATA_NOT_FOUND',
  'ONBOARDING_REFUSED',
  'BUSINESS_INSIGHTS_MATCH_FAILED',
  'BUSINESS_NAME_MATCH_FAILED',
  'PSC_MISSING',
  'PSC_FORM_ALTERED',
  'UNVERIFIED_BUSINESS_PSC',
  'PSC_MATCH_FAILED',
] as const;

const isKnownCheck = (
  type: string | null
): type is (typeof KNOWN_CHECKS)[number] =>
  (KNOWN_CHECKS as ReadonlyArray<string | null>).includes(type);

const isKnownReason = (
  type: string | null
): type is (typeof KNOWN_REASONS)[number] =>
  (KNOWN_REASONS as ReadonlyArray<string | null>).includes(type);

const CheckReason: FC<{ reason: IdvCheckReason }> = ({ reason }) => {
  const { t } = useTranslation();
  return (
    <Typography variant="body2">
      {isKnownReason(reason.type)
        ? t(`kyc.idv.check-reason.${reason.type}`)
        : reason.value ?? reason.type}
    </Typography>
  );
};

const pscName = (psc: IdvPsc): string =>
  [psc.first_name, psc.last_name].filter(Boolean).join(' ');

interface SessionDetailsProps {
  session: IdvSession;
  pscActions: IdvPscAction[];
  onPscAction: (action: IdvPscAction) => void;
  onCopyPscLink: (url: string) => void;
  disabled: boolean;
}

const SessionDetails: FC<SessionDetailsProps> = ({
  session,
  pscActions,
  onPscAction,
  onCopyPscLink,
  disabled,
}) => {
  const { t } = useTranslation();
  const refusedChecks = session.checks.filter(
    (check) => check.status === 'REFUSED'
  );

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      {refusedChecks.length > 0 && (
        <Alert severity="error">
          <Typography variant="subtitle2">
            {t('kyc.idv.refused-checks')}
          </Typography>
          {refusedChecks.map((check, index) => (
            <Box
              key={`${check.type}-${index}`}
              sx={{
                mt: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {isKnownCheck(check.type)
                  ? t(`kyc.idv.check.${check.type}`)
                  : check.type}
              </Typography>
              {check.reasons.map((reason, reasonIndex) => (
                <CheckReason key={reasonIndex} reason={reason} />
              ))}
            </Box>
          ))}
        </Alert>
      )}

      {session.pscs.length > 0 && (
        <Stack
          sx={{
            gap: 1.5,
          }}
        >
          <Typography variant="subtitle2">{t('kyc.idv.pscs')}</Typography>
          {session.pscs.map((psc) => {
            const pscAction = pscActions.find(
              (candidate) => candidate.psc.id === psc.id
            );
            return (
              <Stack
                key={psc.id}
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {pscName(psc) || psc.business_name || t('kyc.idv.psc')}
                    </Typography>
                    <Label variant="ghost" color={PSC_COLORS[psc.status]}>
                      {t(`kyc.idv.psc-status.${psc.status}`)}
                    </Label>
                  </Stack>
                  {psc.business_name && pscName(psc) && (
                    <Typography
                      variant="caption"
                      noWrap
                      title={psc.business_name}
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                      }}
                    >
                      {psc.business_name}
                    </Typography>
                  )}
                </Box>
                {pscAction && (
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {pscAction.action === 'open' && psc.hosted_url && (
                      <PscCopyLinkButton
                        url={psc.hosted_url}
                        onCopy={onCopyPscLink}
                      />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={disabled}
                      onClick={() => onPscAction(pscAction)}
                    >
                      {pscAction.action === 'open'
                        ? t('kyc.idv.psc-open')
                        : t('kyc.idv.psc-retry')}
                    </Button>
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

const PscCopyLinkButton: FC<{
  url: string;
  onCopy: (url: string) => void;
}> = ({ url, onCopy }) => {
  const { t } = useTranslation();
  const label = t('kyc.idv.psc-copy-link');
  return (
    <Tooltip title={label}>
      <IconButton size="small" aria-label={label} onClick={() => onCopy(url)}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};
